import * as React from 'react'
import {DataGrid, GridToolbar} from '@mui/x-data-grid'
import axios from 'axios'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import Typography from '@mui/material/Typography'
import './BILAwards.css'

function escapeRegExp(value: string): string {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
}

interface QuickSearchToolbarProps {
  clearSearch: () => void
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  value: string
}

function QuickSearchToolbar(props: QuickSearchToolbarProps) {
  return (
    <Box
      sx={{
        p: 0.5,
        pb: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <TextField
        variant='standard'
        value={props.value}
        onChange={props.onChange}
        placeholder='Search…'
        InputProps={{
          startAdornment: <SearchIcon fontSize='small' />,
          endAdornment: (
            <IconButton
              title='Clear'
              aria-label='Clear'
              size='small'
              style={{visibility: props.value ? 'visible' : 'hidden'}}
              onClick={props.clearSearch}
            >
              <ClearIcon fontSize='small' />
            </IconButton>
          ),
        }}
      />
      <GridToolbar />
    </Box>
  )
}

export default function ConstructionLaborSkillsTransferability() {
  const [data, setData] = React.useState<any[]>([])
  const [searchText, setSearchText] = React.useState('')
  const [rows, setRows] = React.useState<any[]>([])

  React.useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + '/cl_skills_transfer') // Replace with your endpoint
      .then((response) => {
        setData(response.data)
      })
      .catch((error) => {
        console.error('There was an error!', error)
      })
  }, [])

  React.useEffect(() => {
    setRows(
      data.map((row, index) => ({
        ...row,
        id: index + 1,
      }))
    )
  }, [data])

  const requestSearch = (searchValue: string) => {
    setSearchText(searchValue)
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i')
    const filteredRows = data
      .map((row, index) => ({
        ...row,
        id: index + 1,
      }))
      .filter((row: any) => {
        return Object.keys(row).some((field: any) => {
          if (row[field] !== null && row[field] !== undefined) {
            return searchRegex.test(row[field].toString())
          } else {
            return false
          }
        })
      })
    setRows(filteredRows)
  }

  type ColumnNames = {
    'O*NET': string
    'O*NET Occupation': string
    'Median Hourly Earnings*': string
    '2022 Jobs*': string
    '2023 Jobs*': string
    '2022-2023 Change*': string
    '2022-2023 Estimated Annual Openings*': string
    'Compatibility Index': string
  }

  const columnNames: ColumnNames = {
    'O*NET': 'O*NET',
    'O*NET Occupation': 'O*NET Occupation',
    'Median Hourly Earnings*': 'Median Hourly Earnings*',
    '2022 Jobs*': '2022 Jobs*',
    '2023 Jobs*': '2023 Jobs*',
    '2022-2023 Change*': '2022-2023 Change*',
    '2022-2023 Estimated Annual Openings*': '2022-2023 Estimated Annual Openings*',
    'Compatibility Index': 'Compatibility Index',
  }

  const columnOrder: (keyof ColumnNames)[] = [
    'O*NET',
    'O*NET Occupation',
    'Median Hourly Earnings*',
    '2022 Jobs*',
    '2023 Jobs*',
    '2022-2023 Change*',
    '2022-2023 Estimated Annual Openings*',
    'Compatibility Index',
  ]

  const columns = columnOrder.map((key, index) => {
    let column: any = {
      field: key,
      headerName: columnNames[key] || key,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    }

    // If the current column is the first, second, or "Compatibility Index", make it wider
    if (index === 0 || index === 1 || key === 'Compatibility Index') {
      column = {...column, flex: 2}
    }

    // If the current column is "Compatibility Index", render its cell content in bold
    if (key === 'Compatibility Index') {
      column = {
        ...column,
        renderCell: (params: any) => <strong>{params.value}</strong>,
      }
    }

    return column
  })

  return (
    <div>
      <Typography variant='h3' align='center' gutterBottom>
        Construction Labor Skills Transferability
      </Typography>
      <br />
      <br />
      <DataGrid
        rows={rows}
        columns={columns}
        components={{Toolbar: QuickSearchToolbar}}
        componentsProps={{
          toolbar: {
            value: searchText,
            onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
              requestSearch(event.target.value),
            clearSearch: () => requestSearch(''),
          },
        }}
        className='datagrid'
        getRowClassName={(params) => (Number(params.id) % 2 === 0 ? 'even-row' : 'odd-row')}
      />
      <Typography variant='body2' align='center' gutterBottom style={{marginTop: '1em'}}>
        Lightcast Labor Market Data (2023). Construction Labor Skills Transferability. Retrieved
        from{' '}
        <a
          href='https://lightcast.io/' // Update with your source
          target='_blank'
          rel='noopener noreferrer'
        >
          https://lightcast.io/ Q3 Data Pull
        </a>
        , Date Retrieved 03/20/2024
      </Typography>
    </div>
  )
}
